import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { onIdTokenChanged, signInWithEmailAndPassword, signOut, type User } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import { auth } from "./firebase.js";

interface AuthState {
  user: User | null;
  loading: boolean;
  signIn(email: string, password: string): Promise<void>;
  signOutNow(): Promise<void>;
  /** Fresh ID token for API calls. Firebase refreshes it when near expiry. */
  getToken(): Promise<string | null>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  /**
   * The uid the cache currently holds data for.
   *
   * `undefined` means "nothing observed yet", which is different from `null`
   * ("observed, signed out") — on a page load we must not treat the first
   * observation as a change, or every refresh would discard the cache and reset
   * the route the user deep-linked to.
   */
  const cachedFor = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    // onIdTokenChanged rather than onAuthStateChanged: it also fires on token
    // refresh, which is what keeps a long-lived tab from making API calls with an
    // expired token after an hour.
    return onIdTokenChanged(auth, (next) => {
      const previous = cachedFor.current;
      const uid = next?.uid ?? null;

      /**
       * Everything cached belongs to whoever was signed in when it was fetched.
       *
       * None of the 86 query keys in this app carries a user id — they are keyed
       * by name and org — so without this, signing out and back in as somebody
       * else serves the previous person's session and rows from cache. That is
       * not a cosmetic staleness: `["me"]` carries the role, so a bookkeeper who
       * signed in after an owner was shown the owner's identity and the owner's
       * unfiltered client list, and `staleTime` meant it did not correct itself
       * for thirty seconds. Sometimes a refetch landed and it flipped; usually it
       * did not, which is what made it look intermittent.
       *
       * Clearing here rather than adding the uid to every key is deliberate: one
       * rule covers keys that already exist and keys nobody has written yet.
       *
       * Comparing uids matters. This callback also fires on the hourly token
       * refresh with the same user, and clearing then would throw away the whole
       * cache and restart every query on a timer.
       */
      if (previous !== undefined && previous !== uid) {
        queryClient.clear();
      }

      cachedFor.current = uid;
      setUser(next);
      setLoading(false);
    });
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      async signIn(email, password) {
        await signInWithEmailAndPassword(auth, email, password);
      },
      async signOutNow() {
        // Cleared here as well as in the listener above: the listener is what
        // makes every path safe, but doing it eagerly means the sign-in screen is
        // never rendered over a cache still holding the previous user's data.
        await signOut(auth);
        queryClient.clear();
      },
      async getToken() {
        return auth.currentUser ? auth.currentUser.getIdToken() : null;
      },
    }),
    [user, loading, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
